import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Statistic,
  Timeline,
  Tag,
  Spin,
  Modal,
  Select,
  Input,
  notification,
  Tabs,
  Table,
  Badge,
  Space,
  Empty,
  Drawer
} from 'antd';
import {
  ClockCircleOutlined,
  CoffeeOutlined,
  PlusOutlined,
  LogoutOutlined,
  LoginOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import * as workService from './workTrackingService';
import './WorkTracking.css';
import { getCurrentStaffId } from '../../authHelper';
import AddManualActivity from './AddManualActivity';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const StaffDashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);
  const [breakModalVisible, setBreakModalVisible] = useState(false);
  const [breakTypes, setBreakTypes] = useState({});
  const [selectedBreakType, setSelectedBreakType] = useState('');
  const [breakNotes, setBreakNotes] = useState('');
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Helper function to format time to local timezone
  const formatLocalTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid time';
    }
  };

  const formatLocalDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  useEffect(() => {
    const staffId = getCurrentStaffId();
    
    console.log('Staff ID retrieved:', staffId);
    
    if (staffId) {
      setCurrentStaffId(staffId);
      fetchData(staffId);
      loadBreakTypes();

      const interval = setInterval(() => fetchData(staffId, true), 60000);
      return () => clearInterval(interval);
    } else {
      console.error('Unable to retrieve staff ID');
      setLoading(false);
      notification.error({
        message: 'Authentication Error',
        description: 'Unable to verify your staff identity. Please log in again.'
      });
    }
  }, []);


  const fetchData = async (staffId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const [statusRes, summaryRes] = await Promise.all([
        workService.getCurrentStatus(staffId),
        workService.getTodaySummary(staffId)
      ]);

      setStatus(statusRes.data);
      setTodaySummary(summaryRes.data);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to load data'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadBreakTypes = async () => {
    try {
      const res = await workService.getBreakTypes();
      setBreakTypes(res.data);
    } catch (error) {
      console.error('Failed to load break types:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      setLoading(true);
      await workService.clockIn(currentStaffId);
      notification.success({
        message: 'Success',
        description: 'Clocked in successfully!'
      });
      await fetchData(currentStaffId);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to clock in'
      });
      setLoading(false);
    }
  };

  const handleClockOut = () => {
    Modal.confirm({
      title: 'Clock Out',
      content: 'Are you sure you want to clock out for today?',
      onOk: async () => {
        try {
          setLoading(true);
          const res = await workService.clockOut(currentStaffId);
          notification.success({
            message: 'Clocked Out Successfully',
            description: (
              <div>
                <div>Total Work Time: {res.data.total_work_time}</div>
                <div>Total Break Time: {res.data.total_break_time}</div>
                <div>Activities: {res.data.activities_today}</div>
              </div>
            ),
            duration: 5
          });
          await fetchData(currentStaffId);
        } catch (error) {
          notification.error({
            message: 'Error',
            description: error.message || 'Failed to clock out'
          });
          setLoading(false);
        }
      }
    });
  };

  const handleStartBreak = async () => {
    if (!selectedBreakType) {
      notification.warning({ message: 'Please select break type' });
      return;
    }

    try {
      setLoading(true);
      await workService.startBreak(currentStaffId, selectedBreakType, breakNotes);
      notification.success({
        message: 'Break Started',
        description: `${selectedBreakType} break started`
      });
      setBreakModalVisible(false);
      setSelectedBreakType('');
      setBreakNotes('');
      await fetchData(currentStaffId);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to start break'
      });
      setLoading(false);
    }
  };

  const handleEndBreak = async () => {
    try {
      setLoading(true);
      const res = await workService.endBreak(currentStaffId);
      notification.success({
        message: 'Break Ended',
        description: `Break duration: ${res.data.break_duration_formatted}`
      });
      await fetchData(currentStaffId);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to end break'
      });
      setLoading(false);
    }
  };

  const getStatusColor = (statusValue) => {
    const colors = {
      'active': 'green',
      'on_break': 'orange',
      'idle': 'volcano',
      'clocked_out': 'default'
    };
    return colors[statusValue] || 'default';
  };

  const getStatusText = (statusValue) => {
    const texts = {
      'active': 'Active',
      'on_break': 'On Break',
      'idle': 'Idle',
      'clocked_out': 'Clocked Out'
    };
    return texts[statusValue] || statusValue;
  };

  if (loading && !status) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const isClockedOut = status?.current_status === 'clocked_out';
  const isOnBreak = status?.current_status === 'on_break';

  return (
    <div className="work-tracking-dashboard">
      {/* Header Card with Status */}
      <Card className="status-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" size="small">
              <h2>
                {refreshing && <LoadingOutlined style={{ marginRight: 8 }} />}
                Work Status
              </h2>
              <Tag color={getStatusColor(status?.current_status)} style={{ fontSize: '16px', padding: '8px 16px' }}>
                {getStatusText(status?.current_status)}
              </Tag>
              {status?.clock_in_time && (
                <div style={{ color: '#666', fontSize: '13px' }}>
                  {status.current_status === 'clocked_out' && status.clock_out_time ? (
                    <>
                      <div>🕐 Clocked in: {formatLocalTime(status.clock_in_time)}</div>
                      <div>🕐 Clocked out: {formatLocalTime(status.clock_out_time)}</div>
                    </>
                  ) : (
                    <div>🕐 Clocked in at: {formatLocalTime(status.clock_in_time)}</div>
                  )}
                </div>
              )}
            </Space>
          </Col>

          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <Statistic
              title="Time Worked Today"
              value={todaySummary?.work_status?.total_work_formatted || status?.time_worked_formatted || '0h 0m'}
              valueStyle={{ color: '#3f8600', fontSize: '28px' }}
              prefix={<ClockCircleOutlined />}
            />
          </Col>

          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            {isClockedOut ? (
              <Button
                type="primary"
                size="large"
                icon={<LoginOutlined />}
                onClick={handleClockIn}
                style={{ background: '#52c41a' }}
              >
                Clock In
              </Button>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {isOnBreak ? (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleEndBreak}
                    style={{ width: '100%' }}
                  >
                    End Break
                  </Button>
                ) : (
                  <Button
                    size="large"
                    icon={<CoffeeOutlined />}
                    onClick={() => setBreakModalVisible(true)}
                    style={{ width: '100%' }}
                  >
                    Take Break
                  </Button>
                )}
                <Button
                  danger
                  size="large"
                  icon={<LogoutOutlined />}
                  onClick={handleClockOut}
                  style={{ width: '100%' }}
                >
                  Clock Out
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {/* Action Buttons */}
      {!isClockedOut && (
        <Card style={{ marginTop: 16 }}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setDrawerOpen(true)}
            size="large"
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
          >
            Add Manual Work Entry
          </Button>
        </Card>
      )}

      {/* Today's Summary */}
      <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Today's Summary</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Statistic
              title="Activities"
              value={todaySummary?.activity_summary?.total_activities || 0}
              prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Break Time"
              value={todaySummary?.work_status?.total_break_formatted || `${todaySummary?.breaks?.total_minutes || 0}m`}
              valueStyle={{ fontSize: '24px' }}
              prefix={<CoffeeOutlined style={{ color: '#fa8c16' }} />}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Productive Time"
              value={todaySummary?.work_status?.productive_formatted || '0h 0m'}
              prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Breaks Taken"
              value={todaySummary?.breaks?.count || 0}
            />
          </Col>
        </Row>
      </Card>

      {/* Activity Summary by Category */}
      {todaySummary?.activity_summary?.by_category?.length > 0 && (
        <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Work by Category</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            {todaySummary.activity_summary.by_category.map((cat, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <Card size="small" style={{ background: '#f5f5f5' }}>
                  <Statistic
                    title={cat.activity_category}
                    value={cat.count}
                    suffix={`activities (${Math.round(cat.total_duration || 0)}m)`}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Activity Timeline */}
      {todaySummary?.timeline?.length > 0 ? (
        <Card
          title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Today's Activity Timeline</span>}
          headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
          extra={
            <Button onClick={() => history.push('/work-tracking/timeline')}>
              View All
            </Button>
          }
          style={{ marginTop: 16 }}
        >
          <Timeline>
            {todaySummary.timeline.slice(0, 5).map((activity, index) => (
              <Timeline.Item
                key={index}
                color={activity.entry_type === 'automatic' ? 'green' : 'blue'}
              >
                <div>
                  <strong>{formatLocalTime(activity.start_time)}</strong>
                  {' - '}
                  <Tag color="blue">{activity.activity_category || 'Unknown'}</Tag>
                  {activity.entry_type === 'automatic' && (
                    <Tag color="green" style={{ marginLeft: 4 }}>Auto</Tag>
                  )}
                </div>
                <div style={{ color: '#666' }}>{activity.description || 'No description'}</div>
                {activity.duration_minutes > 0 && (
                  <div style={{ color: '#999', fontSize: '12px' }}>
                    Duration: {activity.duration_minutes}m
                  </div>
                )}
                {activity.entry_type === 'manual' && (
                  <Tag color="orange" size="small">Manual Entry</Tag>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      ) : (
        !isClockedOut && (
          <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Today's Activity Timeline</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} style={{ marginTop: 16 }}>
            <Empty description="No activities yet today. Start working to see your activity log!" />
          </Card>
        )
      )}

      {/* Add Manual Activity Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000' }}>Add Manual Work Entry</span>}
        width={600}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <AddManualActivity
          onSuccess={() => { setDrawerOpen(false); fetchData(currentStaffId); }}
          onCancel={() => setDrawerOpen(false)}
        />
      </Drawer>

      {/* Break Modal */}
      <Modal
        title="Start Break"
        visible={breakModalVisible}
        onOk={handleStartBreak}
        onCancel={() => {
          setBreakModalVisible(false);
          setSelectedBreakType('');
          setBreakNotes('');
        }}
        okText="Start Break"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label>Break Type *</label>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select break type"
              value={selectedBreakType}
              onChange={setSelectedBreakType}
              size="large"
            >
              {Object.entries(breakTypes).map(([key, value]) => (
                <Option key={key} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <label>Notes (Optional)</label>
            <TextArea
              rows={3}
              placeholder="Any notes about this break..."
              value={breakNotes}
              onChange={(e) => setBreakNotes(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default StaffDashboard;
